import { ClientUser } from '@numeo/types-core'
import { TeamDriverFull, TeamDriverRow, TeamListPage, TeamMemberRow, TeamPerson } from '@numeo/types-main-web-app'
import { TeamDriversQuery, TeamMembersQuery } from '@/utils/team/teamQuery'
import { personFromDriverRow, personFromMemberRow } from '@/utils/team/teamRows'
import { requestApi } from './axiosBaseQuery'
import { baseApi } from './baseApi'
import { loadPerson } from './peopleBatcher'

/**
 * The team directory (`/team/*`): the company's members and drivers a page at a time, a few drivers' whole records for
 * the driver form, and names for ids. Every argument carries the company's `applicationId`, so a cache entry never
 * crosses companies. Read through the hooks in `hooks/team/`, never directly.
 */

interface CompanyArg {
    applicationId: string
}

export type ListTeamMembersArg = CompanyArg & TeamMembersQuery
export type ListTeamDriversArg = CompanyArg & TeamDriversQuery
type TeamPersonArg = CompanyArg & { id: string }

/** The tag a list carries: one per kind, so a save invalidates every page and search of that kind at once. */
export const TEAM_LIST_ID = 'LIST'

const emptyPage = <T>(query: { page?: number; limit?: number }): TeamListPage<T> => ({
    items: [],
    pagination: { total: 0, page: query.page ?? 1, limit: query.limit ?? 0 },
})

const joined = (values: readonly (string | number)[] | undefined): string | undefined => (values?.length ? values.join(',') : undefined)

const people = (applicationId: string, found: (TeamPerson | null)[]) =>
    found
        .filter((person): person is TeamPerson => !!person)
        .map((person) => ({ endpointName: 'getPerson' as const, arg: { applicationId, id: person.id }, value: person }))

export const teamApi = baseApi.injectEndpoints({
    endpoints: (build) => ({
        listTeamMembers: build.query<TeamListPage<TeamMemberRow>, ListTeamMembersArg>({
            queryFn: async (query, { signal }) => {
                // `applicationId` keys the cache only: the server answers for the token's company.
                const result = await requestApi<TeamListPage<TeamMemberRow>>(
                    {
                        url: '/team/members/v1',
                        params: { page: query.page, limit: query.limit, search: query.search, roles: joined(query.roles), ids: joined(query.ids) },
                    },
                    signal
                )
                return 'error' in result ? result : { data: result.data ?? emptyPage(query) }
            },
            providesTags: [{ type: 'TeamMembers', id: TEAM_LIST_ID }],
            // A person's name is cached in one place: the rows a list brings name their ids for every other screen.
            onQueryStarted: async ({ applicationId }, { dispatch, queryFulfilled }) => {
                const fulfilled = await queryFulfilled.catch(() => null) // the hook reports a failed list
                if (fulfilled) dispatch(teamApi.util.upsertQueryEntries(people(applicationId, fulfilled.data.items.map(personFromMemberRow))))
            },
        }),
        listTeamDrivers: build.query<TeamListPage<TeamDriverRow>, ListTeamDriversArg>({
            queryFn: async (query, { signal }) => {
                const result = await requestApi<TeamListPage<TeamDriverRow>>(
                    {
                        url: '/team/drivers/v1',
                        params: {
                            page: query.page,
                            limit: query.limit,
                            search: query.search,
                            searchTrucks: joined(query.searchTruckIds),
                            status: query.status,
                            truck: query.truck,
                            hasCoDriver: query.hasCoDriver,
                            hideTerminated: query.hideTerminated,
                            experience: joined(query.experience),
                            compensation: query.compensation,
                            driverType: query.driverType,
                            sort: query.sort,
                            order: query.order,
                            ids: joined(query.ids),
                        },
                    },
                    signal
                )
                return 'error' in result ? result : { data: result.data ?? emptyPage(query) }
            },
            providesTags: [{ type: 'TeamDrivers', id: TEAM_LIST_ID }],
            onQueryStarted: async ({ applicationId }, { dispatch, queryFulfilled }) => {
                const fulfilled = await queryFulfilled.catch(() => null) // the hook reports a failed list
                if (fulfilled) dispatch(teamApi.util.upsertQueryEntries(people(applicationId, fulfilled.data.items.map(personFromDriverRow))))
            },
        }),
        /** The driver form's whole record of up to 20 drivers (`view=full`, DRIVER_PROFILES:VIEW). */
        getTeamDriversFull: build.query<TeamDriverFull[], CompanyArg & { ids: string[] }>({
            queryFn: async ({ ids }, { signal }) => {
                const result = await requestApi<{ items?: TeamDriverFull[] }>({ url: '/team/drivers/v1', params: { view: 'full', ids: ids.join(',') } }, signal)
                return 'error' in result ? result : { data: result.data?.items ?? [] }
            },
            // Per driver too: a socket change cannot patch a whole record from a base row, so it invalidates the records it touches.
            providesTags: (_result, _error, { ids }) => [
                { type: 'TeamDrivers', id: TEAM_LIST_ID },
                ...ids.map((id) => ({ type: 'TeamDriverFull' as const, id })),
            ],
        }),
        /**
         * The drivers this caller may see, as the Groups-scoped roster serves them (`/team-groups/driver-roster/v1`): the
         * scoped list for a company the directory does not serve yet. Same tag as the directory's driver lists.
         */
        getScopedDriverRoster: build.query<ClientUser[], CompanyArg>({
            queryFn: async (_arg, { signal }) => {
                const result = await requestApi<{ drivers?: ClientUser[] }>({ url: '/team-groups/driver-roster/v1' }, signal)
                return 'error' in result ? result : { data: result.data?.drivers ?? [] }
            },
            providesTags: [{ type: 'TeamDrivers', id: TEAM_LIST_ID }],
        }),
        /** One person by id, batched with every other id asked in the same tick. `null`: nobody by that id. */
        getPerson: build.query<TeamPerson | null, TeamPersonArg>({
            queryFn: ({ id }) => loadPerson(id),
            providesTags: (_result, _error, { id }) => [{ type: 'TeamPeople', id }],
        }),
    }),
})

export const { useListTeamMembersQuery, useListTeamDriversQuery, useGetTeamDriversFullQuery, useGetScopedDriverRosterQuery } = teamApi
